import { useState } from "react";
import { Link } from "react-router-dom";
import { BookmarkPlus, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Shared "Save as Flashcards" button used by every reference chart
 * (Amino Acids, Neurotransmitters, Enzyme Kinetics, Functional Groups).
 *
 * Props:
 *   - sourceChart: stable id like "amino-acids" — deck upsert key per user
 *   - title: human-readable deck title
 *   - cards: [{front, back, meta?}] — passed straight to the backend
 *   - className: optional wrapper tweaks
 */
export default function SaveChartButton({
  sourceChart,
  title,
  cards,
  className = "",
}) {
  const [state, setState] = useState("idle"); // idle | saving | saved
  const [deckId, setDeckId] = useState(null);

  const save = async () => {
    if (state === "saving") return;
    setState("saving");
    try {
      const r = await api.post("/custom-flashcards/save-chart", {
        source_chart: sourceChart,
        title,
        cards,
      });
      setDeckId(r.data.id);
      setState("saved");
      toast.success(
        r.data.created
          ? `Saved as flashcard deck (${cards.length} cards)`
          : `Deck updated (${cards.length} cards)`,
        { description: "Head to My Decks to drill them anytime." }
      );
    } catch (e) {
      setState("idle");
      toast.error(
        e?.response?.data?.detail || "Couldn't save the deck. Try again?"
      );
    }
  };

  if (state === "saved" && deckId) {
    return (
      <Link
        to={`/app/decks/${deckId}`}
        className={`pm-btn-secondary text-xs inline-flex items-center gap-1.5 ${className}`}
        style={{ borderColor: "var(--mastery)", color: "var(--mastery)" }}
        data-testid="save-chart-view-deck"
      >
        <CheckCircle2 size={12} /> Drill this deck →
      </Link>
    );
  }

  return (
    <button
      onClick={save}
      disabled={state === "saving"}
      className={`pm-btn-secondary text-xs inline-flex items-center gap-1.5 ${className}`}
      data-testid="save-chart-btn"
    >
      {state === "saving" ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <BookmarkPlus size={12} />
      )}
      {state === "saving" ? "Saving…" : `Save as ${cards.length} flashcards`}
    </button>
  );
}
